import { supabase, supabaseUrl, supabaseAnonKey } from "@/lib/supabase";

// Centralized API client.
// PUBLIC reads (domains, sales, favorites listing, tracking inserts, inquiry submit) → Supabase RLS-allowed direct calls.
// ADMIN writes/reads (CRUD on domains/sales, reading inquiries, analytics) → admin-api edge function (service role + token auth).
// FAVORITES delete → admin-api public sub-endpoint that validates user_identifier (no admin token required).

// The Data API caps every response at 1000 rows. pageAll() pages through a
// query (or RPC) so counts and lists are never silently truncated.
export async function pageAll<T = any>(query: any, pageSize = 1000, maxRows = 200000): Promise<T[]> {
  const all: T[] = [];
  for (let from = 0; from < maxRows; from += pageSize) {
    const { data, error } = await query.range(from, from + pageSize - 1);
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as T[];
    all.push(...rows);
    if (rows.length < pageSize) break;
  }
  return all;
}

// ───────────── Domains ─────────────
export async function getDomains() {
  return await pageAll(
    supabase.from("domains").select("*").order("created_at", { ascending: false })
  );
}


export async function getDomain(slug: string) {
  const { data, error } = await supabase
    .from("domains")
    .select("*")
    .eq("slug", slug)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Domain not found");
  return data as any;
}

// ───────────── Favorites ─────────────
// Returns aggregate like counts only. Individual user_identifier values are
// intentionally NOT exposed — otherwise anyone could enumerate them and call
// /favorites/remove to delete other users' favorites.
export async function getFavorites(): Promise<{ domain_id: string; count: number }[]> {
  const rows = await pageAll<any>((supabase as any).rpc("get_favorite_counts"));
  return rows.map((r: any) => ({
    domain_id: String(r.domain_id),
    count: Number(r.count) || 0,
  }));
}


// Local mirror of the current anon user's liked domain ids.
const LIKED_KEY = "pdn_liked_v1";
export function getLikedDomainIds(): Set<string> {
  try {
    const raw = localStorage.getItem(LIKED_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr.map(String) : []);
  } catch {
    return new Set();
  }
}
function persistLiked(set: Set<string>) {
  try { localStorage.setItem(LIKED_KEY, JSON.stringify([...set])); } catch {}
}
export function markLiked(domain_id: string | number) {
  const s = getLikedDomainIds(); s.add(String(domain_id)); persistLiked(s);
}
export function markUnliked(domain_id: string | number) {
  const s = getLikedDomainIds(); s.delete(String(domain_id)); persistLiked(s);
}

export async function addFavorite(domain_id: number | string, user_identifier: string) {
  // Best-effort geo + session enrichment (non-blocking on failure)
  let geo: { country?: string; country_code?: string } = {};
  let session_id: string | null = null;
  try {
    const cached = localStorage.getItem("pdn_geo_v1");
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed?.g) geo = parsed.g;
    }
  } catch {}
  if (!geo.country) {
    try {
      const r = await fetch("https://api.country.is/", { cache: "force-cache" });
      if (r.ok) {
        const j = await r.json();
        geo = { country: j?.country, country_code: j?.country };
      }
    } catch {}
  }
  try { session_id = localStorage.getItem("pdn_session_id"); } catch {}

  const { error } = await supabase
    .from("favorites")
    .insert({
      domain_id: String(domain_id),
      user_identifier,
      country: geo.country ?? null,
      country_code: geo.country_code ?? null,
      session_id,
    });
  if (error && !/duplicate/i.test(error.message)) throw new Error(error.message);
  return { ok: true };
}

export async function removeFavorite(domain_id: number | string, user_identifier: string) {
  // Routed through edge function so server validates the user_identifier.
  const res = await fetch(`${supabaseUrl}/functions/v1/admin-api/favorites/remove`, {
    method: "POST",
    headers: { "Content-Type": "application/json", apikey: supabaseAnonKey },
    body: JSON.stringify({ domain_id: String(domain_id), user_identifier }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || "Failed to remove favorite");
  return { ok: true };
}

// ───────────── Inquiries ─────────────
export async function createInquiry(payload: {
  name?: string;
  email: string;
  budget?: string;
  message: string;
  domain_name?: string;
}) {
  const { error } = await supabase.from("inquiries").insert({
    name: payload.name ?? null,
    email: payload.email,
    message: payload.message,
    offer: payload.budget ?? null,
    domain_name: payload.domain_name ?? null,
  });
  if (error) throw new Error(error.message);

  try {
    await fetch(`${supabaseUrl}/functions/v1/quick-api/inquiries/notify`, {
      method: "POST",
      headers: { "Content-Type": "application/json", apikey: supabaseAnonKey },
      body: JSON.stringify(payload),
    });
  } catch {
    // ignore
  }
  return { ok: true };
}

// ───────────── Launched Brands (public) ─────────────
export async function getLaunchedBrands() {
  const { data, error } = await supabase
    .from("launched_brands")
    .select("*")
    .eq("active", true)
    .order("sort_order", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as any[];
}

// ───────────── Marketplaces (public) ─────────────
export async function getMarketplaces() {
  const { data, error } = await supabase
    .from("marketplaces" as any)
    .select("*")
    .eq("active", true)
    .order("sort_order", { ascending: true });
  if (error) return [] as any[];
  return (data ?? []) as any[];
}

// ───────────── Site Settings (public) ─────────────
export async function getSiteSettings() {
  const { data, error } = await supabase
    .from("site_settings" as any)
    .select("key, value");
  if (error) return {} as Record<string, string>;
  const map: Record<string, string> = {};
  for (const row of (data ?? []) as any[]) map[row.key] = row.value ?? "";
  return map;
}

// ───────────── Newsletter ─────────────
export async function subscribeNewsletter(email: string, source = "homepage") {
  const res = await fetch(`${supabaseUrl}/functions/v1/newsletter`, {
    method: "POST",
    headers: { "Content-Type": "application/json", apikey: supabaseAnonKey },
    body: JSON.stringify({ email, source }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok || !data?.ok) throw new Error(data?.error || "Subscription failed");
  return { ok: true };
}

// ───────────── Sales (public read; price is masked server-side when hide_price=true) ─────────────
export async function getSales() {
  // PostgREST caps responses at 1000 rows — page through everything.
  const PAGE = 1000;
  const all: any[] = [];
  for (let page = 0; ; page++) {
    const { data, error } = await supabase
      .rpc("get_public_sales")
      .range(page * PAGE, page * PAGE + PAGE - 1);
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as any[];
    all.push(...rows);
    if (rows.length < PAGE) break;
    if (page > 50) break;
  }
  return all;
}



// ───────────── Blog (public read) ─────────────
export async function getBlogPosts() {
  const { data, error } = await supabase
    .from("blog_posts" as any)
    .select("id, slug, title, excerpt, cover_url, author, tags, published_at, created_at")
    .eq("published", true)
    .order("published_at", { ascending: false, nullsFirst: false });
  if (error) return [] as any[];
  return (data ?? []) as any[];
}

export async function getBlogPost(slug: string) {
  const { data, error } = await supabase
    .from("blog_posts" as any)
    .select("*")
    .eq("slug", slug)
    .eq("published", true)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Post not found");
  return data as any;
}

// ───────────── Tracking ─────────────
export async function trackEvent(domain_id: number | string, type: "view" | "click") {
  try {
    const slug = String(domain_id);
    if (type === "view") {
      await supabase.from("views").insert({ domain_slug: slug });
    } else {
      await supabase.from("clicks").insert({ domain_slug: slug, type: "click" });
    }
  } catch {
    // best-effort
  }
}

// ───────────── Admin Auth ─────────────
const TOKEN_KEY = "admin_token";

export function getAdminToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAdminToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export async function adminLogin(email: string, password: string) {
  const res = await fetch(`${supabaseUrl}/functions/v1/admin-api/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json", apikey: supabaseAnonKey },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  const token = data.data?.token ?? data.token;
  if (!res.ok || !token) throw new Error(data?.error || "Login failed");
  setAdminToken(token);
  return { token, user: data.data?.user ?? data.user };
}

export function adminLogout() {
  setAdminToken(null);
}

export async function adminRequestPasswordReset(email: string) {
  const res = await fetch(`${supabaseUrl}/functions/v1/admin-api/password-reset/request`, {
    method: "POST",
    headers: { "Content-Type": "application/json", apikey: supabaseAnonKey },
    body: JSON.stringify({ email }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || "Request failed");
  return data?.data ?? data;
}

export async function adminConfirmPasswordReset(token: string, newPassword: string) {
  const res = await fetch(`${supabaseUrl}/functions/v1/admin-api/password-reset/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json", apikey: supabaseAnonKey },
    body: JSON.stringify({ token, new_password: newPassword }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || "Reset failed");
  return data?.data ?? data;
}

export async function adminFetch(path: string, init: RequestInit = {}) {
  const token = getAdminToken();
  const edgePath = path.replace(/^\/admin/, "");
  const res = await fetch(`${supabaseUrl}/functions/v1/admin-api${edgePath}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      apikey: supabaseAnonKey,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers || {}),
    },
  });
  const data = await res.json().catch(() => null);
  if (res.status === 401) {
    setAdminToken(null);
    window.dispatchEvent(new Event("pdn-admin-unauthorized"));
  }
  if (!res.ok) {
    const msg = data?.error || data?.message || `Request failed (${res.status})`;
    const err: any = new Error(msg);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  if (data?.ok === true) return data.data;
  if (data && typeof data === "object" && !Array.isArray(data) && !("ok" in data) && "data" in data && Object.keys(data).length === 1) {
    return data.data;
  }
  return data;
}

// ───────────── Admin CRUD helpers ─────────────
export const adminApi = {
  // Domains
  listDomains: () => adminFetch("/domains"),
  createDomain: (payload: any) =>
    adminFetch("/domains", { method: "POST", body: JSON.stringify(payload) }),
  bulkCreateDomains: (items: any[]) =>
    adminFetch("/domains", { method: "POST", body: JSON.stringify(items) }),
  updateDomain: (id: string, payload: any) =>
    adminFetch(`/domains/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  deleteDomain: (id: string) =>
    adminFetch(`/domains/${id}`, { method: "DELETE" }),
  bulkDeleteDomains: (ids: string[]) =>
    adminFetch("/domains/bulk-delete", { method: "POST", body: JSON.stringify({ ids }) }),
  bulkUpdateDomains: (ids: string[], patch: Record<string, any>) =>
    adminFetch("/domains/bulk-update", { method: "POST", body: JSON.stringify({ ids, patch }) }),
  generateDomainInsights: (params: { id?: string; ids?: string[]; scope?: "featured" | "high_priority" | "missing"; tier?: "basic" | "enhanced" | "premium"; force?: boolean; field?: string; fields?: string[] }) =>
    adminFetch("/domains/generate-insights", { method: "POST", body: JSON.stringify(params) }),

  // Sales
  listSales: () => adminFetch("/sales"),
  createSale: (payload: any) =>
    adminFetch("/sales", { method: "POST", body: JSON.stringify(payload) }),
  bulkCreateSales: (items: any[]) =>
    adminFetch("/sales", { method: "POST", body: JSON.stringify(items) }),
  updateSale: (id: string, payload: any) =>
    adminFetch(`/sales/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  bulkUpdateSales: (ids: string[], patch: Record<string, any>) =>
    adminFetch("/sales/bulk-update", { method: "POST", body: JSON.stringify({ ids, patch }) }),
  deleteSale: (id: string) =>
    adminFetch(`/sales/${id}`, { method: "DELETE" }),
  bulkDeleteSales: (ids: string[]) =>
    adminFetch("/sales/bulk-delete", { method: "POST", body: JSON.stringify({ ids }) }),

  // Inquiries
  listInquiries: () => adminFetch("/inquiries"),
  deleteInquiry: (id: string) =>
    adminFetch(`/inquiries/${id}`, { method: "DELETE" }),
  updateInquiry: (id: string, patch: { status?: string; admin_notes?: string }) =>
    adminFetch(`/inquiries/${id}`, { method: "PUT", body: JSON.stringify(patch) }),
  replyInquiry: (id: string, payload: { subject: string; message: string }) =>
    adminFetch(`/inquiries/${id}/reply`, { method: "POST", body: JSON.stringify(payload) }),


  // Saved searches
  listSavedSearches: () => adminFetch("/saved-searches"),
  deleteSavedSearch: (id: string) =>
    adminFetch(`/saved-searches/${id}`, { method: "DELETE" }),

  // A/B test results
  getABResults: () => adminFetch("/ab-results"),


  // Analytics
  getAnalytics: () => adminFetch("/analytics"),
  getVisitors: (days = 30) => adminFetch(`/visitors?days=${days}`),
  getReports: (days = 30) => adminFetch(`/reports?days=${days}`),


  // Password
  changePassword: (current_password: string, new_password: string) =>
    adminFetch("/change-password", {
      method: "POST",
      body: JSON.stringify({ current_password, new_password }),
    }),

  // Launched brands
  listBrands: () => adminFetch("/brands"),
  createBrand: (payload: any) =>
    adminFetch("/brands", { method: "POST", body: JSON.stringify(payload) }),
  updateBrand: (id: string, payload: any) =>
    adminFetch(`/brands/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  deleteBrand: (id: string) =>
    adminFetch(`/brands/${id}`, { method: "DELETE" }),

  // Marketplaces
  listMarketplaces: () => adminFetch("/marketplaces"),
  createMarketplace: (payload: any) =>
    adminFetch("/marketplaces", { method: "POST", body: JSON.stringify(payload) }),
  updateMarketplace: (id: string, payload: any) =>
    adminFetch(`/marketplaces/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  deleteMarketplace: (id: string) =>
    adminFetch(`/marketplaces/${id}`, { method: "DELETE" }),

  // Outreach replies (manual review)
  listReplies: (params: { intent?: string; review?: string; q?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.intent) qs.set("intent", params.intent);
    if (params.review) qs.set("review", params.review);
    if (params.q) qs.set("q", params.q);
    if (params.limit) qs.set("limit", String(params.limit));
    const s = qs.toString();
    return adminFetch(`/replies${s ? `?${s}` : ""}`);
  },
  repliesStats: () => adminFetch("/replies/stats"),
  confirmReply: (id: string) => adminFetch(`/replies/${id}/confirm`, { method: "POST" }),
  overrideReply: (id: string, intent: string) =>
    adminFetch(`/replies/${id}/override`, { method: "POST", body: JSON.stringify({ intent }) }),
  clearReplyOverride: (id: string) => adminFetch(`/replies/${id}/clear`, { method: "POST" }),


  // Logo upload (routes through admin-api, which uses service role to write to storage)
  uploadLogo: async (file: File): Promise<{ url: string; path: string }> => {
    const token = getAdminToken();
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${supabaseUrl}/functions/v1/admin-api/upload-logo`, {
      method: "POST",
      headers: {
        apikey: supabaseAnonKey,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: fd,
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error(data?.error || "Upload failed");
    return data?.ok ? data.data : data;
  },

  // Site settings
  listSettings: () => adminFetch("/settings"),
  updateSettings: (items: { key: string; value: string }[]) =>
    adminFetch("/settings", { method: "PUT", body: JSON.stringify({ items }) }),

  // AI providers
  listAiProviders: () => adminFetch("/ai-providers"),
  updateAiProvider: (payload: { provider: string; api_key?: string; default_model?: string; base_url?: string; enabled?: boolean; notes?: string }) =>
    adminFetch("/ai-providers", { method: "PUT", body: JSON.stringify(payload) }),
  setActiveAiProvider: (provider: string) =>
    adminFetch("/ai-providers/active", { method: "POST", body: JSON.stringify({ provider }) }),
  listAiTasks: () => adminFetch("/ai-tasks"),
  setAiTask: (task: string, provider: string) =>
    adminFetch("/ai-tasks", { method: "POST", body: JSON.stringify({ task, provider }) }),
  testAiProvider: (provider: string) =>
    adminFetch("/ai-providers/test", { method: "POST", body: JSON.stringify({ provider }) }),

  // Newsletter subscribers
  listSubscribers: () => adminFetch("/subscribers"),
  deleteSubscriber: (id: string) =>
    adminFetch(`/subscribers/${id}`, { method: "DELETE" }),
  emailReport: (days = 30) => adminFetch(`/email-report?days=${days}`),

  // Blog
  listBlogPosts: () => adminFetch("/blog"),
  createBlogPost: (payload: any) =>
    adminFetch("/blog", { method: "POST", body: JSON.stringify(payload) }),
  updateBlogPost: (id: string, payload: any) =>
    adminFetch(`/blog/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  deleteBlogPost: (id: string) =>
    adminFetch(`/blog/${id}`, { method: "DELETE" }),
  blogStats: () => adminFetch("/blog-stats"),
};

// ───────────── Outreach API ─────────────
async function outreachFetch(path: string, init: RequestInit = {}) {
  const token = getAdminToken();
  const res = await fetch(`${supabaseUrl}/functions/v1/outreach-api${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      apikey: supabaseAnonKey,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers || {}),
    },
  });
  const data = await res.json().catch(() => null);
  if (res.status === 401) { setAdminToken(null); window.dispatchEvent(new Event("pdn-admin-unauthorized")); }
  if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`);
  return data?.data ?? data;
}

async function outreachFetchRaw(path: string, init: RequestInit = {}) {
  const token = getAdminToken();
  const res = await fetch(`${supabaseUrl}/functions/v1/outreach-api${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      apikey: supabaseAnonKey,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers || {}),
    },
  });
  const data = await res.json().catch(() => null);
  if (res.status === 401) { setAdminToken(null); window.dispatchEvent(new Event("pdn-admin-unauthorized")); }
  if (!res.ok) {
    const err: any = new Error(data?.error || `Request failed (${res.status})`);
    err.body = data;
    throw err;
  }
  return data;
}

export const outreachApi = {
  listProspects: (params: { status?: string; q?: string; domain?: string; campaign_id?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set("status", params.status);
    if (params.q) qs.set("q", params.q);
    if (params.domain) qs.set("domain", params.domain);
    if (params.campaign_id) qs.set("campaign_id", params.campaign_id);
    return outreachFetch(`/prospects${qs.toString() ? `?${qs}` : ""}`);
  },
  createProspects: (items: any[]) => outreachFetch("/prospects", { method: "POST", body: JSON.stringify(items) }),
  updateProspect: (id: string, patch: any) => outreachFetch(`/prospects/${id}`, { method: "PUT", body: JSON.stringify(patch) }),
  deleteProspect: (id: string) => outreachFetch(`/prospects/${id}`, { method: "DELETE" }),
  listCampaigns: () => outreachFetch("/campaigns"),
  getCampaign: (id: string) => outreachFetch(`/campaigns/${id}`),
  createCampaign: (payload: any) => outreachFetch("/campaigns", { method: "POST", body: JSON.stringify(payload) }),
  updateCampaign: (id: string, patch: any) => outreachFetch(`/campaigns/${id}`, { method: "PUT", body: JSON.stringify(patch) }),
  deleteCampaign: (id: string) => outreachFetch(`/campaigns/${id}`, { method: "DELETE" }),
  addSequence: (campaignId: string, payload: any) =>
    outreachFetch(`/campaigns/${campaignId}/sequences`, { method: "POST", body: JSON.stringify(payload) }),
  updateSequence: (id: string, patch: any) => outreachFetch(`/sequences/${id}`, { method: "PUT", body: JSON.stringify(patch) }),
  deleteSequence: (id: string) => outreachFetch(`/sequences/${id}`, { method: "DELETE" }),
  enqueue: (campaignId: string, payload: { prospect_ids?: string[]; all?: boolean }) =>
    outreachFetch(`/campaigns/${campaignId}/enqueue`, { method: "POST", body: JSON.stringify(payload) }),
  deleteQueuedMessages: (campaignId: string) =>
    outreachFetch(`/campaigns/${campaignId}/queued-messages`, { method: "DELETE" }),
  preflight: (campaignId: string, payload: { dry_run?: boolean; sample_limit?: number } = {}) =>
    outreachFetch(`/campaigns/${campaignId}/preflight`, { method: "POST", body: JSON.stringify(payload) }),
  listMessages: (campaignId: string) => outreachFetch(`/campaigns/${campaignId}/messages`),
  listScheduledMessages: (params: { status?: string; q?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set("status", params.status);
    if (params.q) qs.set("q", params.q);
    if (params.limit) qs.set("limit", String(params.limit));
    return outreachFetchRaw(`/scheduled-messages${qs.toString() ? `?${qs}` : ""}`);
  },
  getScheduledMessage: (id: string) => outreachFetchRaw(`/scheduled-messages/${id}`),
  updateScheduledMessage: (id: string, patch: { custom_subject?: string | null; custom_body_html?: string | null; scheduled_at?: string; clear_override?: boolean }) =>
    outreachFetchRaw(`/scheduled-messages/${id}`, { method: "PUT", body: JSON.stringify(patch) }),
  regenerateScheduledMessage: (id: string, opts: { refresh_intel?: boolean } = {}) =>
    outreachFetchRaw(`/scheduled-messages/${id}/regenerate`, { method: "POST", body: JSON.stringify(opts) }),
  personalizeProspect: (prospectId: string, campaign_id?: string | null) =>
    outreachFetch(`/personalize/prospect/${prospectId}`, { method: "POST", body: JSON.stringify({ campaign_id }) }),
  personalizeBatch: (prospect_ids: string[], campaign_id?: string | null) =>
    outreachFetch(`/personalize/batch`, { method: "POST", body: JSON.stringify({ prospect_ids, campaign_id }) }),
  getPersonalization: (prospectId: string, campaign_id?: string | null) =>
    outreachFetch(`/personalize/prospect/${prospectId}${campaign_id ? `?campaign_id=${campaign_id}` : ""}`),
  generateAiCopy: (campaignId: string, sample_prospect_id?: string | null) =>
    outreachFetch(`/campaigns/${campaignId}/ai-copy`, { method: "POST", body: JSON.stringify({ sample_prospect_id }) }),
  scoreCopy: (text: string) =>
    outreachFetch(`/personalize/score`, { method: "POST", body: JSON.stringify({ text }) }),
  generateInitialMessage: (prospectId: string, campaign_id?: string | null) =>
    outreachFetch(`/personalize/initial/prospect/${prospectId}`, { method: "POST", body: JSON.stringify({ campaign_id }) }),
  generateInitialMessagesForCampaign: (campaignId: string, prospect_ids?: string[] | null) =>
    outreachFetch(`/campaigns/${campaignId}/generate-initial-messages`, { method: "POST", body: JSON.stringify({ prospect_ids: prospect_ids || null }) }),
  generateSequence: (campaignId: string, replace = false) =>
    outreachFetch(`/campaigns/${campaignId}/ai-sequence`, { method: "POST", body: JSON.stringify({ replace }) }),
  aiDiscover: (payload: { domain: string; industry?: string; niche?: string; count?: number; min_buyer_score?: number; company_size?: string; funding_stage?: string; min_domain_weakness?: number }) =>
    outreachFetch("/ai-discover", { method: "POST", body: JSON.stringify(payload) }),
  aiInferFields: (domain: string) =>
    outreachFetch("/ai-infer-fields", { method: "POST", body: JSON.stringify({ domain }) }),
  report: (days = 30) => outreachFetch(`/report?days=${days}`),
  listSuppression: () => outreachFetch("/suppression"),
  addSuppression: (email: string, reason = "manual") =>
    outreachFetch("/suppression", { method: "POST", body: JSON.stringify({ email, reason }) }),
  runSender: (campaignId?: string, batchSize = 25) =>
    fetch(`${supabaseUrl}/functions/v1/outreach-sender`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: supabaseAnonKey,
        ...(getAdminToken() ? { Authorization: `Bearer ${getAdminToken()}` } : {}),
      },
      body: JSON.stringify({ campaign_id: campaignId, batch_size: batchSize }),
    }).then(async (r) => {
      const d = await r.json().catch(() => null);
      if (!r.ok) throw new Error(d?.error || "Send failed");
      return d;
    }),
  // ─── v2 ───
  analytics: (days = 30, campaignId?: string) => outreachFetch(`/analytics?days=${days}${campaignId ? `&campaign_id=${campaignId}` : ""}`),
  listAbTests: () => outreachFetch("/ab-tests"),
  createAbTest: (payload: any) => outreachFetch("/ab-tests", { method: "POST", body: JSON.stringify(payload) }),
  decideAbTest: (id: string, winner_variant_id: string) => outreachFetch(`/ab-tests/${id}/decide`, { method: "POST", body: JSON.stringify({ winner_variant_id }) }),
  listReplies: (campaignId?: string) => outreachFetch(`/replies${campaignId ? `?campaign_id=${campaignId}` : ""}`),
  updateReply: (id: string, manual_override: string) => outreachFetch(`/replies/${id}`, { method: "PUT", body: JSON.stringify({ manual_override }) }),
  listDomainHealth: () => outreachFetch("/domain-health"),
  listEmailProviders: (days = 7) => outreachFetch(`/email-providers?days=${days}`),
  resumeEmailProvider: (provider_id: string) =>
    outreachFetch(`/email-providers/resume`, { method: "POST", body: JSON.stringify({ provider_id }) }),
  reorderEmailProviders: (order: string[]) =>
    outreachFetch(`/email-providers/reorder`, { method: "POST", body: JSON.stringify({ order }) }),
  configureSmtp: (payload: { host: string; port: number; username: string; password?: string; from_email: string; from_name?: string; enabled?: boolean; daily_limit?: number }) =>
    outreachFetch(`/email-providers/smtp/configure`, { method: "POST", body: JSON.stringify(payload) }),
  testSmtp: () => outreachFetchRaw(`/email-providers/smtp/test`, { method: "POST", body: "{}" }),
  sendTestSmtp: (to: string) =>
    outreachFetchRaw(`/email-providers/smtp/send-test`, { method: "POST", body: JSON.stringify({ to }) }),
  debugEmailProviders: () => outreachFetchRaw(`/email-providers/debug`),
  getProviderStatus: () => outreachFetch(`/email-providers/status`),
  getProviderSendLog: (opts: { limit?: number; onlyFailover?: boolean } = {}) => {
    const qs = new URLSearchParams();
    if (opts.limit) qs.set("limit", String(opts.limit));
    if (opts.onlyFailover) qs.set("failover", "1");
    const s = qs.toString();
    return outreachFetch(`/email-providers/send-log${s ? `?${s}` : ""}`);
  },
  getPreferredProvider: () => outreachFetch(`/email-providers/preferred`),
  setPreferredProvider: (provider_id: string) =>
    outreachFetch(`/email-providers/preferred`, { method: "POST", body: JSON.stringify({ provider_id }) }),
  getWarmupStatus: (provider_id = "smtp") =>
    outreachFetch(`/email-providers/warmup?provider_id=${encodeURIComponent(provider_id)}`),
  updateWarmup: (payload: Record<string, unknown>) =>
    outreachFetch(`/email-providers/warmup`, { method: "POST", body: JSON.stringify(payload) }),

  // ─── Draft Engine (Generate → Preview → Approve → Send) ───
  listDrafts: (campaign_id: string, opts: { status?: string; step?: number } = {}) => {
    const qs = new URLSearchParams({ campaign_id });
    if (opts.status) qs.set("status", opts.status);
    if (opts.step) qs.set("step", String(opts.step));
    return outreachFetch(`/drafts?${qs.toString()}`);
  },
  getDraft: (draft_id: string) => outreachFetch(`/drafts/${draft_id}`),
  generateDraft: (prospect_id: string, campaign_id: string, step: number) =>
    outreachFetch(`/drafts/generate`, { method: "POST", body: JSON.stringify({ prospect_id, campaign_id, step }) }),
  regenerateDraft: (draft_id: string) =>
    outreachFetch(`/drafts/regenerate`, { method: "POST", body: JSON.stringify({ draft_id }) }),
  approveDraft: (draft_id: string) =>
    outreachFetch(`/drafts/approve`, { method: "POST", body: JSON.stringify({ draft_id }) }),
  rejectDraft: (draft_id: string, reject_reason?: string) =>
    outreachFetch(`/drafts/reject`, { method: "POST", body: JSON.stringify({ draft_id, reject_reason }) }),
  sendDraft: (draft_id: string, recipient_emails?: string[]) =>
    outreachFetch(`/drafts/send`, { method: "POST", body: JSON.stringify({ draft_id, recipient_emails }) }),

  domainHealthAction: (fromEmail: string, action: "pause" | "resume" | "reset" | "update", payload: any = {}) =>
    outreachFetch(`/domain-health/${encodeURIComponent(fromEmail)}/${action}`, { method: "POST", body: JSON.stringify(payload) }),
  listLogs: (params: { level?: string; campaign_id?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.level) qs.set("level", params.level);
    if (params.campaign_id) qs.set("campaign_id", params.campaign_id);
    return outreachFetch(`/logs${qs.toString() ? `?${qs}` : ""}`);
  },
  exportCsvUrl: (type: "messages" | "prospects" | "replies", campaignId?: string) =>
    `${supabaseUrl}/functions/v1/outreach-api/export?type=${type}${campaignId ? `&campaign_id=${campaignId}` : ""}`,

  // ─── Intelligence / AI Learning ───
  intelligence: (days = 30) => outreachFetch(`/intelligence?days=${days}`),
  optimizeVariant: (payload: { winners: any[]; industry?: string; tone?: string }) =>
    outreachFetch(`/intelligence/optimize`, { method: "POST", body: JSON.stringify(payload) }),
  autoPauseLosers: () => outreachFetch(`/intelligence/auto-pause-losers`, { method: "POST" }),
  performance: (days = 30) => outreachFetch(`/intelligence/performance?days=${days}`),


  // ─── Portfolio Intelligence ───
  portfolioIntel: (days = 90) => outreachFetch(`/portfolio-intel?days=${days}`),
  portfolioFocus: (domain_ids: string[]) =>
    outreachFetch(`/portfolio-intel/focus`, { method: "POST", body: JSON.stringify({ domain_ids }) }),

  // ─── AI Deal Engine ───
  listDeals: (params: { stage?: string; q?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.stage) qs.set("stage", params.stage);
    if (params.q) qs.set("q", params.q);
    return outreachFetch(`/deals${qs.toString() ? `?${qs}` : ""}`);
  },
  createDeal: (payload: any) => outreachFetch(`/deals`, { method: "POST", body: JSON.stringify(payload) }),
  getDeal: (id: string) => outreachFetch(`/deals/${id}`),
  updateDeal: (id: string, patch: any) => outreachFetch(`/deals/${id}`, { method: "PUT", body: JSON.stringify(patch) }),
  deleteDeal: (id: string) => outreachFetch(`/deals/${id}`, { method: "DELETE" }),
  addDealActivity: (id: string, payload: any) => outreachFetch(`/deals/${id}/activities`, { method: "POST", body: JSON.stringify(payload) }),
  addDealReminder: (id: string, payload: { due_at: string; message: string }) => outreachFetch(`/deals/${id}/reminders`, { method: "POST", body: JSON.stringify(payload) }),
  updateReminder: (id: string, patch: any) => outreachFetch(`/reminders/${id}`, { method: "PUT", body: JSON.stringify(patch) }),
  deleteReminder: (id: string) => outreachFetch(`/reminders/${id}`, { method: "DELETE" }),
  addDealNegotiation: (id: string, payload: any) => outreachFetch(`/deals/${id}/negotiations`, { method: "POST", body: JSON.stringify(payload) }),
  dealPriceIntel: (input: any) => outreachFetch(`/deals/ai/price-intel`, { method: "POST", body: JSON.stringify({ input }) }),
  dealGenerateReply: (payload: { deal_id?: string; scenario: string; buyer_message?: string; custom_instructions?: string }) => outreachFetch(`/deals/ai/generate-reply`, { method: "POST", body: JSON.stringify(payload) }),
  dealFollowup: (deal_id: string) => outreachFetch(`/deals/ai/followup`, { method: "POST", body: JSON.stringify({ deal_id }) }),
  dealDashboard: (days = 90) => outreachFetch(`/deals/dashboard?days=${days}`),


  // ─── Email verification ───
  verifySingle: (email: string) =>
    fetch(`${supabaseUrl}/functions/v1/outreach-verify/verify-single`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: supabaseAnonKey,
        ...(getAdminToken() ? { Authorization: `Bearer ${getAdminToken()}` } : {}),
      },
      body: JSON.stringify({ email }),
    }).then(async (r) => {
      const d = await r.json().catch(() => null);
      if (!r.ok) throw new Error(d?.error || "Verification failed");
      return d?.data;
    }),
  verifyProspects: (payload: { prospect_ids?: string[]; all_unverified?: boolean; force?: boolean; batch_size?: number }) =>
    fetch(`${supabaseUrl}/functions/v1/outreach-verify/verify`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: supabaseAnonKey,
        ...(getAdminToken() ? { Authorization: `Bearer ${getAdminToken()}` } : {}),
      },
      body: JSON.stringify(payload),
    }).then(async (r) => {
      const d = await r.json().catch(() => null);
      if (!r.ok) throw new Error(d?.error || "Verification failed");
      return d?.data;
    }),
  verificationStats: () =>
    fetch(`${supabaseUrl}/functions/v1/outreach-verify/stats`, {
      headers: { apikey: supabaseAnonKey, ...(getAdminToken() ? { Authorization: `Bearer ${getAdminToken()}` } : {}) },
    }).then(async (r) => (await r.json())?.data),
  getVerificationSettings: () =>
    fetch(`${supabaseUrl}/functions/v1/outreach-verify/settings`, {
      headers: { apikey: supabaseAnonKey, ...(getAdminToken() ? { Authorization: `Bearer ${getAdminToken()}` } : {}) },
    }).then(async (r) => (await r.json())?.data),
  updateVerificationSettings: (patch: Record<string, unknown>) =>
    fetch(`${supabaseUrl}/functions/v1/outreach-verify/settings`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        apikey: supabaseAnonKey,
        ...(getAdminToken() ? { Authorization: `Bearer ${getAdminToken()}` } : {}),
      },
      body: JSON.stringify(patch),
    }).then(async (r) => (await r.json())?.data),

  checkWebsites: async (urls: string[]) => {
    const res = await fetch(`${supabaseUrl}/functions/v1/website-check`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: supabaseAnonKey,
        ...(getAdminToken() ? { Authorization: `Bearer ${getAdminToken()}` } : {}),
      },
      body: JSON.stringify({ urls }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`);
    return (data?.results || []) as Array<{ url: string; final_url?: string; ok: boolean; status: number; error?: string; latency_ms?: number; nameservers?: string[]; for_sale?: boolean; for_sale_reason?: string | null }>;
  },

};

// ───────────── Lead Discovery API ─────────────
async function leadFetch(path: string, init: RequestInit = {}) {
  const token = getAdminToken();
  const res = await fetch(`${supabaseUrl}/functions/v1/lead-discovery${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      apikey: supabaseAnonKey,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers || {}),
    },
  });
  const data = await res.json().catch(() => null);
  if (res.status === 401) { setAdminToken(null); window.dispatchEvent(new Event("pdn-admin-unauthorized")); }
  if (!res.ok) {
    const err: any = new Error(data?.error || `Request failed (${res.status})`);
    err.status = res.status;
    err.data = data?.data;
    throw err;
  }
  return data?.data ?? data;
}

function classifyEdgeFailure(status: number, data: any): string {
  if (status === 401 || status === 403) return "auth issue";
  if (status === 404 && data?.code === "NOT_FOUND") return "function missing";
  if (status === 404 && data?.reason) return String(data.reason);
  if (status === 404) return "route mismatch";
  if (status >= 500) return "deployment failed";
  if (status === 400) return "malformed URL";
  return data?.reason || "backend error";
}

async function hunterLogsFetch(limit = 200) {
  const token = getAdminToken();
  const safeLimit = Math.max(1, Math.min(500, Math.floor(Number(limit) || 200)));
  const url = `${supabaseUrl}/functions/v1/hunter-logs?limit=${safeLimit}`;
  console.info("[Hunter Logs] Calling backend URL:", url);
  const res = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      apikey: supabaseAnonKey,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const data = await res.json().catch(() => null);
  if (res.status === 401) { setAdminToken(null); window.dispatchEvent(new Event("pdn-admin-unauthorized")); }
  if (!res.ok) {
    const reason = classifyEdgeFailure(res.status, data);
    const message = data?.error || data?.message || `Request failed (${res.status})`;
    const err: any = new Error(`${message} — reason: ${reason}`);
    err.status = res.status;
    err.reason = reason;
    err.url = url;
    err.data = data;
    throw err;
  }
  const payload = data?.data ?? data;
  return { ...payload, _debugUrl: url };
}

export const leadDiscoveryApi = {
  status: () => leadFetch("/status"),
  quota: () => leadFetch("/quota"),
  list: (domain?: string) => leadFetch(`/leads${domain ? `?domain=${encodeURIComponent(domain)}` : ""}`),
  discover: (domain: string, opts: { limit?: number; force?: boolean } = {}) =>
    leadFetch("/discover", { method: "POST", body: JSON.stringify({ domain, ...opts }) }),
  updateSettings: (patch: { enable_hunter?: boolean; enable_prospeo?: boolean; active_provider?: string; cache_ttl_hours?: number; per_admin_daily_limit?: number; global_daily_limit?: number; hunter_daily_limit?: number; prospeo_daily_limit?: number }) =>
    leadFetch("/settings", { method: "POST", body: JSON.stringify(patch) }),
  feedback: (lead_id: string, rating: "good" | "bad", note?: string) =>
    leadFetch("/feedback", { method: "POST", body: JSON.stringify({ lead_id, rating, note }) }),
  feedbackMap: (domain?: string) =>
    leadFetch(`/feedback${domain ? `?domain=${encodeURIComponent(domain)}` : ""}`),
  analytics: () => leadFetch("/analytics"),
  training: () => leadFetch("/training"),
  hunterLogs: (limit = 200) => hunterLogsFetch(limit),
};



